import { Formik, type FormikConfig, type FormikErrors, type FormikProps } from "formik";
import { isUndefined, omitBy } from "lodash";
import { useEffect, useRef, useState, type JSX } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ProfileFormInput,
  ProfileFormRadioGroup,
  ProfileFormSelect,
  SubmitButton,
} from "components/ProfileForm/ProfileFormInputs";
import {
  SerializeForm,
  sliceProfileValues,
  type FormValues,
} from "components/ProfileForm/support";
import { PageShell } from "components/PageShell";
import { PageTitle } from "components/PageTitle";
import { useProfileUpdate } from "pages/useProfileUpdate";
import {
  Banner,
  Button,
  Card,
  CardTitle,
  Input,
  Spinner,
  typography,
} from "@dsaui/components";
import { cn } from "@dsaui/utils";
import * as API from "lib/api/types";
import routes from "routes";
import { EMAIL_UPDATES_ENABLED, States } from "utils/constants";
import { useAppContext } from "utils/hooks";
import { validatePresent, validateZip } from "utils/validators";

const personalInformationFormKeys = [
  "chosenFirstName",
  "chosenLastName",
  "phone",
  "mailingLine1",
  "mailingLine2",
  "mailingCity",
  "mailingState",
  "mailingZip",
  "mailingCountry",
  "mailingPreference",
] as const satisfies ReadonlyArray<keyof FormValues>;

type PersonalInformationFormValues = Pick<
  FormValues,
  (typeof personalInformationFormKeys)[number]
>;

const sectionClassName = "flex flex-col gap-6";
const MOBILE_PHONE_TYPE = "mobile";
const methodActionClassName =
  "min-h-11 w-full !border !border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50 focus-visible:!ring-gray-300 sm:w-64";

interface CredentialDetail extends API.SignInCredentialSummary {
  displayLabel: string;
}

interface SignInMethodAction {
  ariaLabel?: string;
  label: string;
  onClick: () => Promise<void>;
}

const readPendingRemoval = (): CredentialDetail | null => {
  if (
    new URLSearchParams(window.location.search).get("credential-removal") !== "confirm"
  ) {
    return null;
  }

  const storedCredential = window.sessionStorage.getItem(
    "mydsa.pendingCredentialRemoval",
  );
  if (!storedCredential) return null;

  try {
    return JSON.parse(storedCredential) as CredentialDetail;
  } catch {
    window.sessionStorage.removeItem("mydsa.pendingCredentialRemoval");
    return null;
  }
};

interface SignInMethodRowProps {
  actions?: SignInMethodAction[];
  configured?: boolean;
  canRemoveCredential?: (credential: CredentialDetail) => boolean;
  credentialDetails?: CredentialDetail[];
  description: string;
  disabled?: boolean;
  emphasisLabel?: string;
  loading: boolean;
  onRemoveCredential?: (credential: CredentialDetail) => void;
  removalLabels?: {
    action: string;
    remove: (label: string) => string;
    unavailable: string;
  };
  statusLabels: {
    active: string;
    checking: string;
    notSetUp: string;
    unavailable: string;
  };
  statusLabel?: string;
  title: string;
  unavailable: boolean;
}

const SignInMethodRow = ({
  actions = [],
  canRemoveCredential,
  configured,
  credentialDetails = [],
  description,
  disabled = false,
  emphasisLabel,
  loading,
  onRemoveCredential,
  removalLabels,
  statusLabels,
  statusLabel,
  title,
  unavailable,
}: SignInMethodRowProps): JSX.Element => {
  const status =
    statusLabel ??
    (loading
      ? statusLabels.checking
      : unavailable
        ? statusLabels.unavailable
        : configured
          ? statusLabels.active
          : statusLabels.notSetUp);

  return (
    <div className="flex flex-col gap-4 py-5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="m-0 text-base font-semibold text-gray-900">{title}</h3>
          {emphasisLabel && (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
              {emphasisLabel}
            </span>
          )}
          <span className="rounded-full border border-gray-300 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700">
            {status}
          </span>
        </div>
        <p className="mt-1 mb-0 text-sm leading-6 text-gray-600">{description}</p>
        {credentialDetails.length > 0 && (
          <ul className="mt-3 mb-0 flex list-none flex-col gap-1 p-0 text-sm text-gray-700">
            {credentialDetails.map((credential, index) => (
              <li
                className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                key={credential.removalHandle ?? `${credential.displayLabel}-${index}`}
              >
                <span className="min-w-0 break-words">{credential.displayLabel}</span>
                {credential.removalHandle &&
                  onRemoveCredential &&
                  removalLabels &&
                  (canRemoveCredential?.(credential) ? (
                    <button
                      aria-label={removalLabels.remove(credential.displayLabel)}
                      className="min-h-11 shrink-0 self-start font-semibold text-red-700 underline decoration-red-300 underline-offset-4 hover:text-red-900 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 sm:min-h-0 sm:self-auto"
                      disabled={disabled}
                      onClick={() => void onRemoveCredential(credential)}
                      type="button"
                    >
                      {removalLabels.action}
                    </button>
                  ) : (
                    <span className="text-xs leading-5 text-gray-600">
                      {removalLabels.unavailable}
                    </span>
                  ))}
              </li>
            ))}
          </ul>
        )}
      </div>
      {actions.length > 0 && (
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-64">
          {actions.map((action) => (
            <Button
              aria-label={action.ariaLabel}
              className={methodActionClassName}
              disabled={disabled}
              key={action.label}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

const validateForm = (
  values: PersonalInformationFormValues,
): FormikErrors<PersonalInformationFormValues> => {
  const errors: FormikErrors<PersonalInformationFormValues> = {
    chosenFirstName: values.chosenFirstName
      ? undefined
      : "errors.forms.memberFirstName",
    chosenLastName: values.chosenLastName ? undefined : "errors.forms.memberLastName",
    mailingLine1: validatePresent("mailingLine1", values),
    mailingCity: validatePresent("mailingCity", values),
    mailingState: validatePresent("mailingState", values),
    mailingZip: validateZip("mailingZip", values, { required: true }),
  };

  return omitBy(errors, isUndefined);
};

const AccountPage = (): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { api, keycloak } = useAppContext();
  const recoveryComplete =
    new URLSearchParams(window.location.search).get("recovery") === "complete";
  const [signInMethods, setSignInMethods] = useState<API.SignInMethods>();
  const [signInMethodsState, setSignInMethodsState] = useState<
    "loading" | "ready" | "unavailable"
  >("loading");
  const [pendingRemoval, setPendingRemoval] = useState<CredentialDetail | null>(
    readPendingRemoval,
  );
  const [removalError, setRemovalError] = useState<string | null>(null);
  const [reauthenticationRequired, setReauthenticationRequired] = useState(false);
  const [removalStatus, setRemovalStatus] = useState<string | null>(null);
  const [removingCredential, setRemovingCredential] = useState(false);
  const removalTitleRef = useRef<HTMLHeadingElement>(null);
  const {
    fullProfileFormValues,
    profile,
    profileReady,
    ready,
    resetPassword,
    submitProfileUpdate,
  } = useProfileUpdate();

  useEffect(() => {
    let active = true;

    const loadSignInMethods = async (): Promise<void> => {
      const [err, methods] = await api.getSignInMethods();
      if (!active) return;

      if (err || !methods) {
        console.error(err ?? new Error("Sign-in method status was empty"));
        setSignInMethodsState("unavailable");
        return;
      }

      setSignInMethods(methods);
      setSignInMethodsState("ready");
    };

    void loadSignInMethods();

    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    if (!pendingRemoval) return;
    window.setTimeout(() => removalTitleRef.current?.focus(), 0);
  }, [pendingRemoval]);

  useEffect(() => {
    if (!recoveryComplete || !profileReady) return;
    window.setTimeout(
      () => document.getElementById("sign-in-methods-title")?.focus(),
      0,
    );
  }, [profileReady, recoveryComplete]);

  if (!profileReady || !fullProfileFormValues) {
    return (
      <PageShell className="max-w-3xl">
        <PageTitle>{t("pages.account")}</PageTitle>
        <Spinner className="mt-3 self-center" />
      </PageShell>
    );
  }

  const mobilePhone = profile?.phones?.find(
    (phone) => phone.type === MOBILE_PHONE_TYPE,
  );
  const initialValues = {
    ...sliceProfileValues(personalInformationFormKeys, fullProfileFormValues),
    phone: mobilePhone?.number ?? "",
  };
  const isMergedAccount = profile?.memberStatus === API.MemberStatus.MERGED;
  const mergedAccountMessage = profile?.mergedPrimaryEmail
    ? t("messages.mergedAccount.withEmail", { email: profile.mergedPrimaryEmail })
    : t("messages.mergedAccount.default");

  const submitEmailUpdate = async (): Promise<void> => {
    await keycloak.login({
      action: "UPDATE_EMAIL",
      redirectUri: `${window.location.origin}${routes.internal.account}`,
    });
  };

  const submitTotpSetup = async (): Promise<void> => {
    await keycloak.login({
      action: "CONFIGURE_TOTP",
      redirectUri: `${window.location.origin}${routes.internal.account}`,
    });
  };

  const submitPasskeySetup = async (): Promise<void> => {
    await keycloak.login({
      action: "webauthn-register-passwordless",
      redirectUri: `${window.location.origin}${routes.internal.account}`,
    });
  };

  const credentialDetails = (
    type: API.SignInCredentialSummary["type"],
  ): CredentialDetail[] =>
    (signInMethods?.credentials ?? [])
      .filter((credential) => credential.type === type)
      .map((credential) => {
        const fallbackLabel = t(`account.signInMethods.credentials.${type}`);
        const label = credential.label?.trim() || fallbackLabel;
        if (!credential.createdAt) return { ...credential, displayLabel: label };

        const date = new Intl.DateTimeFormat(i18n.language, {
          dateStyle: "medium",
        }).format(new Date(credential.createdAt));
        return {
          ...credential,
          displayLabel: t("account.signInMethods.credentials.added", { date, label }),
        };
      });

  const canRemoveCredential = (credential: CredentialDetail): boolean => {
    const remaining = (signInMethods?.credentials ?? []).filter(
      (candidate) => candidate.removalHandle !== credential.removalHandle,
    );
    return (
      remaining.some((candidate) => candidate.type === "passkey") ||
      (remaining.some((candidate) => candidate.type === "password") &&
        remaining.some((candidate) => candidate.type === "totp"))
    );
  };

  const beginCredentialRemoval = (credential: CredentialDetail): void => {
    setReauthenticationRequired(false);
    setRemovalError(null);
    setRemovalStatus(null);
    window.sessionStorage.setItem(
      "mydsa.pendingCredentialRemoval",
      JSON.stringify(credential),
    );
    setPendingRemoval(credential);
    window.history.replaceState(
      {},
      "",
      `${routes.internal.account}?credential-removal=confirm`,
    );
  };

  const reauthenticateForCredentialRemoval = async (): Promise<void> => {
    if (!pendingRemoval) return;

    await keycloak.login({
      maxAge: 0,
      prompt: "login",
      redirectUri: `${window.location.origin}${routes.internal.account}?credential-removal=confirm`,
    });
  };

  const cancelCredentialRemoval = (): void => {
    window.sessionStorage.removeItem("mydsa.pendingCredentialRemoval");
    setPendingRemoval(null);
    setRemovalError(null);
    setReauthenticationRequired(false);
    window.history.replaceState({}, "", routes.internal.account);
  };

  const confirmCredentialRemoval = async (): Promise<void> => {
    if (!pendingRemoval?.removalHandle) return;

    setRemovingCredential(true);
    setRemovalError(null);
    const [err] = await api.removeSignInMethod(pendingRemoval.removalHandle);
    setRemovingCredential(false);

    if (err) {
      const needsReauthentication =
        err.key === "STRONG_AUTH_REQUIRED" ||
        err.key === "CURRENT_SESSION_NOT_FOUND" ||
        err.statusCode === API.HTTPResponse.Forbidden;
      setReauthenticationRequired(needsReauthentication);
      setRemovalError(
        needsReauthentication
          ? t("account.signInMethods.removal.reauthenticate")
          : err.key === "LAST_SECURE_SIGN_IN_METHOD"
            ? t("account.signInMethods.credentials.removeUnavailable")
            : t("account.signInMethods.removal.error"),
      );
      return;
    }

    const removedLabel = pendingRemoval.displayLabel;
    cancelCredentialRemoval();
    setRemovalStatus(
      t("account.signInMethods.removal.success", { credential: removedLabel }),
    );
    const [loadError, methods] = await api.getSignInMethods();
    if (loadError || !methods) {
      console.error(loadError ?? new Error("Sign-in method status was empty"));
      setSignInMethodsState("unavailable");
      return;
    }
    setSignInMethods(methods);
  };

  const submitForm: FormikConfig<PersonalInformationFormValues>["onSubmit"] = async (
    values,
    { setSubmitting },
  ): Promise<void> => {
    try {
      await submitProfileUpdate({
        ...SerializeForm.valuesToProfile(values),
        phones: [
          {
            id: mobilePhone?.id,
            number: values.phone ?? "",
            type: MOBILE_PHONE_TYPE,
          },
        ],
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const methodStatusLabels = {
    active: t("account.signInMethods.status.active"),
    checking: t("account.signInMethods.status.checking"),
    notSetUp: t("account.signInMethods.status.notSetUp"),
    unavailable: t("account.signInMethods.status.unavailable"),
  };
  const strongFallbackConfigured = Boolean(
    signInMethods?.password && signInMethods?.totp,
  );
  const strongFallbackPartiallyConfigured = Boolean(
    signInMethods?.password || signInMethods?.totp,
  );
  const strongFallbackActions: SignInMethodAction[] = [];

  if (!signInMethods?.password) {
    strongFallbackActions.push({
      label: t("account.signInMethods.password.add"),
      onClick: resetPassword,
    });
  }
  if (!signInMethods?.totp) {
    strongFallbackActions.push({
      label: t("account.signInMethods.totp.add"),
      onClick: submitTotpSetup,
    });
  }
  if (strongFallbackConfigured) {
    strongFallbackActions.push({
      label: t("account.signInMethods.totp.addAnother"),
      onClick: submitTotpSetup,
    });
  }
  if (signInMethods?.password) {
    strongFallbackActions.push({
      label: t("account.signInMethods.password.change"),
      onClick: resetPassword,
    });
  }

  const strongFallbackDescription = signInMethods?.password
    ? signInMethods.totp
      ? t("account.signInMethods.strongFallback.descriptionActive")
      : t("account.signInMethods.strongFallback.descriptionMissingAuthenticator")
    : signInMethods?.totp
      ? t("account.signInMethods.strongFallback.descriptionMissingPassword")
      : t("account.signInMethods.strongFallback.descriptionNotSetUp");
  const emailFallbackActions: SignInMethodAction[] = signInMethods?.password
    ? []
    : [
        {
          ariaLabel: t("account.signInMethods.emailFallback.addPassword"),
          label: t("account.signInMethods.password.add"),
          onClick: resetPassword,
        },
      ];

  const signInMethodsCard = (
    <Card aria-labelledby="sign-in-methods-title" className="flex flex-col gap-5">
      <div>
        <CardTitle
          className="scroll-mt-6 focus:outline-none"
          id="sign-in-methods-title"
          tabIndex={-1}
        >
          {t("account.signInMethods.title")}
        </CardTitle>
        <p className="mt-2 mb-0 text-sm leading-6 text-gray-600">
          {t("account.signInMethods.description")}
        </p>
      </div>
      {pendingRemoval && (
        <section
          aria-labelledby="credential-removal-title"
          className="rounded-xl border border-red-200 bg-red-50 p-5"
        >
          <h3
            className="m-0 text-lg font-semibold text-gray-900 focus:outline-none"
            id="credential-removal-title"
            ref={removalTitleRef}
            tabIndex={-1}
          >
            {t("account.signInMethods.removal.title")}
          </h3>
          <p className="mt-2 mb-0 leading-6 text-gray-700">
            {t("account.signInMethods.removal.description", {
              credential: pendingRemoval.displayLabel,
            })}
          </p>
          {removalError && (
            <p className="mt-3 mb-0 font-medium text-red-800" role="alert">
              {removalError}
            </p>
          )}
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            {reauthenticationRequired ? (
              <Button
                className="w-full sm:w-auto"
                onClick={() => void reauthenticateForCredentialRemoval()}
              >
                {t("account.signInMethods.removal.signInAgain")}
              </Button>
            ) : (
              <Button
                className="w-full sm:w-auto"
                disabled={removingCredential}
                onClick={() => void confirmCredentialRemoval()}
              >
                {removingCredential
                  ? t("loading")
                  : t("account.signInMethods.removal.confirm")}
              </Button>
            )}
            <Button
              className={methodActionClassName}
              disabled={removingCredential}
              onClick={async () => cancelCredentialRemoval()}
            >
              {t("account.signInMethods.removal.cancel")}
            </Button>
          </div>
        </section>
      )}
      {removalStatus && (
        <p
          className="m-0 rounded-md border border-green-200 bg-green-50 p-3 text-sm leading-6 text-green-900"
          role="status"
        >
          {removalStatus}
        </p>
      )}
      {signInMethodsState === "unavailable" && (
        <p
          className="m-0 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm leading-6 text-gray-700"
          role="status"
        >
          {t("account.signInMethods.unavailable")}
        </p>
      )}
      <div aria-live="polite" className="divide-y divide-gray-200">
        <SignInMethodRow
          actions={[
            {
              label: t(
                signInMethods?.passkey
                  ? "account.signInMethods.passkey.addAnother"
                  : "account.signInMethods.passkey.add",
              ),
              onClick: submitPasskeySetup,
            },
          ]}
          canRemoveCredential={canRemoveCredential}
          configured={signInMethods?.passkey}
          credentialDetails={credentialDetails("passkey")}
          description={t("account.signInMethods.passkey.description")}
          disabled={isMergedAccount}
          emphasisLabel={t("account.signInMethods.recommended")}
          loading={signInMethodsState === "loading"}
          onRemoveCredential={beginCredentialRemoval}
          removalLabels={{
            action: t("account.signInMethods.credentials.removeAction"),
            remove: (label) => t("account.signInMethods.credentials.remove", { label }),
            unavailable: t("account.signInMethods.credentials.removeUnavailable"),
          }}
          statusLabels={methodStatusLabels}
          title={t("account.signInMethods.passkey.title")}
          unavailable={signInMethodsState === "unavailable"}
        />
        <SignInMethodRow
          actions={strongFallbackActions}
          canRemoveCredential={canRemoveCredential}
          configured={strongFallbackConfigured}
          credentialDetails={[
            ...credentialDetails("password"),
            ...credentialDetails("totp"),
          ]}
          description={strongFallbackDescription}
          disabled={isMergedAccount}
          loading={signInMethodsState === "loading"}
          onRemoveCredential={beginCredentialRemoval}
          removalLabels={{
            action: t("account.signInMethods.credentials.removeAction"),
            remove: (label) => t("account.signInMethods.credentials.remove", { label }),
            unavailable: t("account.signInMethods.credentials.removeUnavailable"),
          }}
          statusLabel={
            signInMethodsState === "ready" &&
            strongFallbackPartiallyConfigured &&
            !strongFallbackConfigured
              ? t("account.signInMethods.status.finishSetup")
              : undefined
          }
          statusLabels={methodStatusLabels}
          title={t("account.signInMethods.strongFallback.title")}
          unavailable={signInMethodsState === "unavailable"}
        />
        <SignInMethodRow
          actions={emailFallbackActions}
          configured={signInMethods?.password}
          description={t(
            signInMethods?.password
              ? "account.signInMethods.emailFallback.descriptionAvailable"
              : "account.signInMethods.emailFallback.descriptionNotSetUp",
          )}
          disabled={isMergedAccount}
          emphasisLabel={
            signInMethods?.password
              ? t("account.signInMethods.emailFallback.badgeAvailable")
              : undefined
          }
          loading={signInMethodsState === "loading"}
          statusLabels={{
            ...methodStatusLabels,
            active: t("account.signInMethods.status.available"),
          }}
          title={t("account.signInMethods.emailFallback.title")}
          unavailable={signInMethodsState === "unavailable"}
        />
      </div>
    </Card>
  );

  return (
    <PageShell className="max-w-3xl">
      <PageTitle>{t("pages.account")}</PageTitle>
      {isMergedAccount && (
        <Banner variant="warning">
          <p className="m-0">{mergedAccountMessage}</p>
        </Banner>
      )}
      {recoveryComplete && (
        <Banner title={t("account.recoveryComplete.title")} variant="success">
          <p className="m-0 leading-6">{t("account.recoveryComplete.description")}</p>
        </Banner>
      )}
      {recoveryComplete && signInMethodsCard}
      <Card className="flex flex-col gap-4">
        <CardTitle>{t("account.settings.title")}</CardTitle>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <Input
              autoComplete="email"
              disabled
              id="account-email"
              label={t("account.email")}
              name="accountEmail"
              readOnly
              value={fullProfileFormValues.email ?? ""}
            />
          </div>
          {EMAIL_UPDATES_ENABLED && (
            <Button
              className="mb-4 shrink-0 sm:w-60"
              disabled={isMergedAccount}
              onClick={submitEmailUpdate}
            >
              {t("account.changeEmail")}
            </Button>
          )}
        </div>
      </Card>

      <Formik<PersonalInformationFormValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={submitForm}
        validate={validateForm}
        validateOnMount
      >
        {(fp: FormikProps<PersonalInformationFormValues>) => (
          <form aria-label={t("account.form.title")} onSubmit={fp.handleSubmit}>
            <Card className="flex flex-col gap-8">
              <CardTitle>{t("account.form.title")}</CardTitle>
              <fieldset
                className="contents"
                disabled={isMergedAccount}
                name="profile-fields"
              >
                <section className={sectionClassName}>
                  <CardTitle>{t("account.name")}</CardTitle>
                  <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.account.firstName")}
                        name="chosenFirstName"
                        placeholder=""
                        required
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.account.lastName")}
                        name="chosenLastName"
                        placeholder=""
                        required
                      />
                    </div>
                  </div>
                </section>

                <section className={sectionClassName}>
                  <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("account.phone")}
                        name="phone"
                        type="tel"
                      />
                    </div>
                  </div>
                </section>

                <section className={sectionClassName}>
                  <CardTitle>{t("account.mailingAddress")}</CardTitle>
                  <Banner>
                    <div className="whitespace-pre-line">
                      <Trans
                        components={{
                          1: (
                            <a
                              className={cn(
                                typography.profileLink.className,
                                "break-words",
                              )}
                              href={routes.external.changeChapter}
                              rel="noreferrer"
                              target="_blank"
                            >
                              here
                            </a>
                          ),
                        }}
                        i18nKey="forms.preferences.mailingAddress.banner"
                      />
                    </div>
                  </Banner>

                  <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.labels.mailingLine1")}
                        name="mailingLine1"
                        required
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.labels.mailingLine2")}
                        name="mailingLine2"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.labels.mailingCity")}
                        name="mailingCity"
                        required
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <ProfileFormSelect
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.labels.mailingState")}
                        name="mailingState"
                        required
                      >
                        {States.map((choice) => (
                          <option key={choice} value={choice}>
                            {t(`countries.US.states.${choice}.displayName`)}
                          </option>
                        ))}
                      </ProfileFormSelect>
                    </div>
                    <div className="min-w-0 flex-1">
                      <ProfileFormInput
                        fp={fp as FormikProps<FormValues>}
                        label={t("forms.labels.mailingZip")}
                        maxLength={10}
                        name="mailingZip"
                        required
                      />
                    </div>
                  </div>
                </section>

                <section className={sectionClassName}>
                  <CardTitle>{t("account.mailingPreferences")}</CardTitle>
                  <ProfileFormRadioGroup
                    fp={fp as FormikProps<FormValues>}
                    name="mailingPreference"
                    options={Object.values(API.MailingPreference)}
                  />
                </section>
              </fieldset>

              <div>
                <SubmitButton
                  block
                  fp={fp as FormikProps<FormValues>}
                  ready={ready && !isMergedAccount}
                >
                  {t("account.save")}
                </SubmitButton>
              </div>
            </Card>
          </form>
        )}
      </Formik>
      {!recoveryComplete && signInMethodsCard}
    </PageShell>
  );
};

export default AccountPage;
